import { useState, useEffect } from "react";
import { QuoteSummary } from "@/components/webadvisor/QuoteSummary";
import { CompetitorIntel } from "@/components/webadvisor/CompetitorIntel";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Globe, Search, TrendingUp, AlertTriangle, CheckCircle2, Zap, Target, FileText, ArrowRight, Sparkles, BarChart3, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { LogoUploader } from "@/components/LogoUploader";
import { WEB_ADVISOR_LOGO, fileUrl } from "@/lib/products";
import { PlanActions } from "@/components/webadvisor/PlanActions";
import { LandingPackage } from "@/components/webadvisor/LandingPackage";
import { PacketPanel } from "@/components/webadvisor/PacketPanel";

const SEV_COLORS = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

export function ScoreGauge({ score, size = 140, label }) {
  const radius = (size - 20) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? "#16a34a" : score >= 60 ? "#eab308" : score >= 40 ? "#f97316" : "#dc2626";
  return (
    <div className="flex flex-col items-center" data-testid={`score-gauge-${label || "overall"}`}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} stroke="#e2e8f0" strokeWidth="10" fill="none" />
        <circle cx={size/2} cy={size/2} r={radius} stroke={color} strokeWidth="10" fill="none"
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s" }} />
      </svg>
      <div className="absolute flex flex-col items-center" style={{ marginTop: size / 2 - 20 }}>
        <span className="text-3xl font-black" style={{ color }}>{score}</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">/ 100</span>
      </div>
      {label && <p className="text-xs mt-2 text-slate-600 font-medium">{label}</p>}
    </div>
  );
}

function CategoryBar({ name, score }) {
  const color = score >= 80 ? "bg-green-500" : score >= 60 ? "bg-yellow-500" : score >= 40 ? "bg-orange-500" : "bg-red-500";
  return (
    <div className="mb-3" data-testid={`cat-${name.toLowerCase().replace(/ /g,"-")}`}>
      <div className="flex justify-between text-xs mb-1">
        <span className="font-medium text-slate-700">{name}</span>
        <span className="font-bold text-slate-900">{score}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
}

export default function WebAdvisorPage() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState(auditId ? "view" : "landing");
  const [form, setForm] = useState({ url: "", email: "", name: "", phone: "", business_name: "", industry: "", location: "" });
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planForm, setPlanForm] = useState({ goals: "", budget_monthly: 500, target_locations: "", services: "" });

  useEffect(() => {
    if (!auditId) { setMode("landing"); setAudit(null); setPlan(null); return; }
    if (auditId) {
      setMode("view");
      api.get(`/api/webadvisor/audit/${auditId}`)
        .then(r => {
          setAudit(r.data);
          if (r.data.latest_plan_id) api.get(`/api/webadvisor/plan/${r.data.latest_plan_id}`).then(p => setPlan(p.data.plan)).catch(() => {});
        })
        .catch(() => toast.error("Audit not found"));
    }
  }, [auditId]);

  const submitAudit = async (e) => {
    e.preventDefault();
    if (!form.url) { toast.error("URL required"); return; }
    setLoading(true);
    try {
      const r = await api.post("/api/webadvisor/audit", form);
      toast.success("Audit complete!");
      navigate(`/webadvisor/audit/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Audit failed");
    } finally {
      setLoading(false);
    }
  };

  const generatePlan = async () => {
    if (!audit) return;
    setPlanLoading(true);
    try {
      const { data } = await api.post(`/api/webadvisor/audit/${audit.id}/generate-plan`, {
        goals: planForm.goals.split(",").map(s => s.trim()).filter(Boolean),
        budget_monthly: Number(planForm.budget_monthly),
        target_locations: planForm.target_locations.split(",").map(s => s.trim()).filter(Boolean),
        services: planForm.services.split(",").map(s => s.trim()).filter(Boolean),
      });
      for (let i = 0; i < 60; i++) {
        await new Promise(res => setTimeout(res, 4000));
        const { data: r } = await api.get(`/api/webadvisor/plan/${data.id}`);
        if (r.plan.status === "failed") throw new Error(r.plan.error || "Plan generation failed");
        if (r.plan.status !== "generating") { setPlan(r.plan); toast.success("Growth plan generated!"); return; }
      }
      throw new Error("Plan is taking longer than expected — reload this page in a minute");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Plan generation failed");
    } finally {
      setPlanLoading(false);
    }
  };

  const saveAuditLogo = async (url) => {
    await api.put(`/api/webadvisor/audit/${audit.id}/logo`, { logo_url: url });
    setAudit(a => ({ ...a, logo_url: url }));
  };



  // ===== LANDING =====
  if (mode === "landing") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-16 px-6" data-testid="webadvisor-landing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <img src={WEB_ADVISOR_LOGO} alt="Web Advisor AI" className="h-40 sm:h-48 w-auto mx-auto mb-4 object-contain" data-testid="webadvisor-brand-logo" />
            <Link to="/webadvisor/leads" className="inline-block text-sm font-medium text-blue-600 hover:text-blue-500 mb-4" data-testid="webadvisor-leads-link">View prospect pipeline →</Link><br />
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide mb-4">
              <Sparkles className="w-3.5 h-3.5" /> AI-POWERED WEBSITE AUDIT
            </div>
            <h1 className="sr-only">Web Advisor AI</h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              Enter your website. Get a free technical audit, personalized growth plan, and realistic revenue forecast — powered by Claude Opus 5.5.
            </p>
          </div>

          <form onSubmit={submitAudit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-xl max-w-3xl mx-auto" data-testid="audit-form">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
              <div className="md:col-span-4">
                <Label className="text-sm font-medium text-slate-700">Website URL *</Label>
                <div className="relative">
                  <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-10 border-slate-300" placeholder="https://yoursite.com" value={form.url} onChange={e => setForm({...form, url: e.target.value})} required data-testid="audit-url-input" />
                </div>
              </div>
              <div className="flex items-end">
                <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="audit-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {loading ? "Scanning..." : "Scan"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
              <Input className="border-slate-300" placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="audit-name-input" />
              <Input type="email" className="border-slate-300" placeholder="Email (for report)" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="audit-email-input" />
              <Input className="border-slate-300" placeholder="Business name" value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} />
              <Input className="border-slate-300" placeholder="Industry" value={form.industry} onChange={e => setForm({...form, industry: e.target.value})} />
            </div>
            <p className="text-[11px] text-slate-400 mt-3 text-center">Free. No credit card. Takes ~15 seconds.</p>
          </form>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14 max-w-5xl mx-auto">
            {[
              { icon: <Search className="w-5 h-5" />, title: "Technical SEO Scan", desc: "HTTPS, speed, metadata, structured data, mobile, broken links — all scored.", color: "bg-blue-50 text-blue-600" },
              { icon: <Target className="w-5 h-5" />, title: "AI Growth Plan", desc: "Claude Opus 5.5 analyzes findings & builds a personalized landing page strategy.", color: "bg-purple-50 text-purple-600" },
              { icon: <TrendingUp className="w-5 h-5" />, title: "3 Revenue Scenarios", desc: "Conservative, baseline, optimistic forecasts with assumptions clearly shown.", color: "bg-green-50 text-green-600" },
            ].map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${f.color}`}>{f.icon}</div>
                <h3 className="font-bold text-slate-900 mb-1">{f.title}</h3>
                <p className="text-sm text-slate-500">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!audit) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  // ===== AUDIT RESULTS =====
  const { scores, findings } = audit;
  const sevKeys = ["critical", "high", "medium", "low"];

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-16 px-4 md:px-8" data-testid="webadvisor-results">
      <div className="max-w-7xl mx-auto">
        {/* HEADER */}
        <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            {audit.logo_url && <img src={fileUrl(audit.logo_url)} alt="Customer logo" className="w-20 h-20 object-contain rounded-xl bg-white border border-slate-200 p-1 shrink-0" data-testid="audit-customer-logo" />}
            <div className="min-w-0">
              <Link to="/webadvisor" className="text-sm text-blue-600 hover:underline mb-2 inline-block">← New audit</Link>
              <h1 className="text-3xl font-black text-slate-900">{audit.business_name || audit.url}</h1>
              <p className="text-slate-500 text-sm break-all">{audit.url}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <LogoUploader logoUrl={audit.logo_url} onSave={saveAuditLogo} testIdPrefix="audit-logo" label="Add customer logo" />
            <img src={WEB_ADVISOR_LOGO} alt="Web Advisor AI" className="h-16 w-auto object-contain hidden md:block" />
          </div>
        </div>

        {/* SCORE OVERVIEW */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm flex flex-col items-center">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-3">Overall Score</p>
            <div className="relative"><ScoreGauge score={scores.overall_score} size={160} /></div>
          </div>
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-3">Category Scores</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              {Object.entries(scores.category_scores).map(([name, score]) => (
                <CategoryBar key={name} name={name} score={score} />
              ))}
            </div>
          </div>
        </div>

        {/* ISSUE COUNTS */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="issue-counts">
          {sevKeys.map(k => (
            <div key={k} className={`border rounded-xl p-4 ${SEV_COLORS[k]}`}>
              <p className="text-xs uppercase tracking-wider font-bold opacity-70">{k}</p>
              <p className="text-2xl font-black">{scores.issue_counts[k] || 0}</p>
            </div>
          ))}
          <div className="bg-blue-100 border border-blue-200 text-blue-700 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider font-bold opacity-70">Total Opportunities</p>
            <p className="text-2xl font-black">{scores.total_issues}</p>
          </div>
        </div>

        {/* SITE METRICS */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" /> Site Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
            {[
              ["HTTPS", findings.https ? "✓" : "✗", findings.https ? "text-green-600" : "text-red-600"],
              ["Response", `${findings.response_time_ms}ms`, ""],
              ["Page Size", `${findings.page_size_kb} KB`, ""],
              ["Word Count", findings.word_count, ""],
              ["H1s", findings.h1_count, ""],
              ["H2s", findings.h2_count, ""],
              ["Images", findings.image_count, ""],
              ["Missing Alt", findings.images_missing_alt, findings.images_missing_alt ? "text-orange-600" : "text-green-600"],
              ["Internal Links", findings.internal_links, ""],
              ["External Links", findings.external_links, ""],
              ["Sitemap", findings.has_sitemap ? "✓" : "✗", findings.has_sitemap ? "text-green-600" : "text-red-600"],
              ["Mobile", findings.has_viewport ? "✓" : "✗", findings.has_viewport ? "text-green-600" : "text-red-600"],
            ].map(([label, val, cls]) => (
              <div key={label}>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</p>
                <p className={`font-bold text-slate-900 ${cls}`}>{val}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ISSUES LIST */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6 shadow-sm" data-testid="issues-list">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-orange-500" /> Findings & Recommended Fixes</h2>
          {sevKeys.map(sev => (
            scores.issues[sev]?.length > 0 && (
              <div key={sev} className="mb-5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{sev} ({scores.issues[sev].length})</h3>
                <div className="space-y-2">
                  {scores.issues[sev].map((iss, i) => (
                    <div key={i} className={`border rounded-lg p-3 ${SEV_COLORS[sev]}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm">{iss.message}</p>
                          <p className="text-xs opacity-70 mt-0.5 break-all">{iss.category} · {iss.evidence}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>

        <CompetitorIntel auditId={audit.id} categories={Object.keys(scores.category_scores)} />

        {/* GENERATE AI GROWTH PLAN */}
        <div className="bg-gradient-to-br from-blue-600 to-purple-700 text-white rounded-2xl p-6 sm:p-8 mb-6 shadow-xl" data-testid="plan-generator">
          <h2 className="text-2xl font-black mb-2 flex items-center gap-2"><Sparkles className="w-6 h-6" /> Generate Personalized Growth Plan</h2>
          <p className="text-blue-100 text-sm mb-5">Claude Opus 5.5 analyzes your audit + inputs to build a tailored landing page strategy, keyword plan, roadmap, and 3-scenario revenue forecast.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div><Label className="text-xs text-blue-100">Goals (comma-separated)</Label><Input className="bg-white/10 border-white/20 text-white placeholder:text-blue-200" placeholder="More leads, local visibility..." value={planForm.goals} onChange={e => setPlanForm({...planForm, goals: e.target.value})} data-testid="plan-goals-input" /></div>
            <div><Label className="text-xs text-blue-100">Monthly Budget ($)</Label><Input type="number" className="bg-white/10 border-white/20 text-white" value={planForm.budget_monthly} onChange={e => setPlanForm({...planForm, budget_monthly: e.target.value})} /></div>
            <div><Label className="text-xs text-blue-100">Target Locations</Label><Input className="bg-white/10 border-white/20 text-white placeholder:text-blue-200" placeholder="Austin TX, Round Rock..." value={planForm.target_locations} onChange={e => setPlanForm({...planForm, target_locations: e.target.value})} /></div>
            <div><Label className="text-xs text-blue-100">Services</Label><Input className="bg-white/10 border-white/20 text-white placeholder:text-blue-200" placeholder="Collision repair, paint..." value={planForm.services} onChange={e => setPlanForm({...planForm, services: e.target.value})} /></div>
          </div>
          <button onClick={generatePlan} disabled={planLoading} className="bg-white text-blue-700 font-bold px-6 py-3 rounded-lg hover:bg-blue-50 disabled:opacity-50 flex items-center gap-2" data-testid="generate-plan-btn">
            {planLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {planLoading ? "Generating with Claude Opus 5.5..." : "Generate Growth Plan"}
          </button>
        </div>

        {/* PLAN DISPLAY */}
        <LandingPackage audit={audit} />
        {plan && <PlanActions plan={plan} audit={audit} onPlanChange={setPlan} />}
        {plan?.plan && <PacketPanel plan={plan} />}
        {plan && <PlanDisplay plan={plan} audit={audit} />}
      </div>
    </div>
  );
}

export function PlanDisplay({ plan, audit, invoice }) {
  const p = plan.plan;
  return (
    <div className="space-y-5" data-testid="plan-display">
      {/* Proposal header */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-center justify-between gap-4" data-testid="proposal-header">
        <div className="flex items-center gap-3 min-w-0">
          {audit?.logo_url && <img src={fileUrl(audit.logo_url)} alt="Customer logo" className="h-14 w-auto max-w-[140px] object-contain" data-testid="proposal-customer-logo" />}
          <div className="min-w-0"><p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Growth Proposal for</p><p className="font-bold text-slate-900 truncate">{audit?.business_name || audit?.url}</p></div>
        </div>
        <img src={WEB_ADVISOR_LOGO} alt="Web Advisor AI" className="h-14 w-auto object-contain" />
      </div>
      {/* Executive Summary */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><FileText className="w-5 h-5 text-blue-600" /> Executive Summary</h3>
        <p className="text-slate-700 leading-relaxed">{p.executive_summary}</p>
      </div>

      {/* What's Wrong + Fix First */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white border border-red-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" /> What's Wrong</h3>
          <ul className="space-y-2">
            {(p.what_is_wrong || []).map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><span className="text-red-500 mt-0.5">•</span>{item}</li>
            ))}
          </ul>
          {p.why_it_matters && <p className="text-xs text-slate-500 italic mt-4 pt-3 border-t border-slate-100">{p.why_it_matters}</p>}
        </div>
        <div className="bg-white border border-green-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><Zap className="w-5 h-5 text-green-500" /> Fix First</h3>
          <ul className="space-y-2">
            {(p.fix_first || []).map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />{item}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Landing Pages */}
      {p.landing_pages?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-purple-600" /> Recommended Landing Pages ({p.landing_pages.length})</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {p.landing_pages.map((lp, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h4 className="font-bold text-slate-900 text-sm">{lp.title}</h4>
                  <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full">P{Math.ceil(lp.priority_score / 20)}</span>
                </div>
                <p className="text-xs text-slate-500 mb-2 break-all">/{(lp.url_slug || "").replace(/^\/+/, "")}</p>
                <div className="flex flex-wrap gap-1.5 text-[10px] mb-2">
                  <span className="bg-slate-100 px-2 py-0.5 rounded">{lp.page_type}</span>
                  <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{lp.intent}</span>
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">{lp.estimated_demand} demand</span>
                  <span className="text-slate-500">{lp.estimated_effort_hours}h</span>
                </div>
                <p className="text-xs text-slate-600"><strong>KW:</strong> {lp.primary_keyword}</p>
                <p className="text-xs text-slate-500 mt-1">{lp.meta_description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Keywords */}
      {p.keyword_opportunities?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-3">Keyword Opportunities</h3>
          <div className="flex flex-wrap gap-2">
            {p.keyword_opportunities.map((kw, i) => (
              <div key={i} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-xs">
                <span className="font-medium text-slate-900">{kw.keyword}</span>
                <span className="text-slate-400 ml-2">· {kw.intent}</span>
                <span className={`ml-2 ${kw.difficulty === "low" ? "text-green-600" : kw.difficulty === "high" ? "text-red-600" : "text-yellow-600"}`}>{kw.difficulty}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3 Forecast Scenarios */}
      {p.forecast && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="forecast-section">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-green-600" /> Growth Forecast — 3 Scenarios</h3>
          <p className="text-xs text-slate-400 mb-4 italic">Estimates based on your audit + inputs. Not guaranteed. See assumptions below.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {["conservative", "baseline", "optimistic"].map(sc => {
              const s = p.forecast[sc];
              if (!s) return null;
              const colors = { conservative: "border-slate-300", baseline: "border-blue-400", optimistic: "border-green-400" };
              return (
                <div key={sc} className={`border-2 rounded-xl p-4 ${colors[sc]}`} data-testid={`forecast-${sc}`}>
                  <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">{sc}</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">Monthly Visits</span><span className="font-bold text-slate-900">+{s.new_monthly_visits?.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Monthly Leads</span><span className="font-bold text-slate-900">+{s.new_monthly_leads}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">New Customers</span><span className="font-bold text-slate-900">+{s.new_customers}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Monthly Rev</span><span className="font-bold text-green-600">${s.monthly_revenue?.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Annual Rev</span><span className="font-bold text-green-600">${s.annual_revenue?.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Timeline</span><span className="font-bold text-slate-900">{s.ranking_timeline_months} mo</span></div>
                    <div className="flex justify-between pt-2 border-t"><span className="text-slate-500">ROI</span><span className="font-bold text-blue-600">{s.roi_pct}%</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Roadmap */}
      {p.roadmap && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Implementation Roadmap</h3>
          {Object.entries(p.roadmap).map(([phase, tasks]) => (
            tasks?.length > 0 && (
              <div key={phase} className="mb-4">
                <h4 className="text-sm font-bold text-blue-600 mb-2">{phase.replace(/_/g, " ").toUpperCase()}</h4>
                <div className="space-y-1.5">
                  {tasks.map((t, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-slate-700 border-l-2 border-blue-200 pl-3">
                      <ArrowRight className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
                      <div className="flex-1"><span className="font-medium">{t.task}</span><span className="text-xs text-slate-400 ml-2">· {t.owner} · {t.effort_hours}h · due in {t.due_days}d</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Pricing/Proposal */}
      <QuoteSummary planId={plan.id} invoice={invoice} monthly={p.monthly_management_fee_usd} />

      {p.assumptions?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-xs text-yellow-800">
          <p className="font-bold mb-1">Assumptions used in forecast:</p>
          <ul className="list-disc list-inside space-y-0.5">{p.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
